<script lang="ts">
import WelcomeBonusBanner from '@/components/banners/items/WelcomeBonusBanner.vue'
import DailyGameCard from '@/components/cards/DailyGameCard.vue'
import SidebarLink from '@/components/links/SidebarLink.vue'
import DepositWelcomeModal from '@/components/modals/DepositWelcomeModal.vue'
import { usePromotions } from '@/composables/usePromotions'
import { useRouteMeta } from '@/plugins/useRouteMeta'
import { SettingsService } from '@/services/settings'
import { useInstantWinsStore } from '@/stores/instant-wins'
import type { InstantWinDraw, NavLinkCategory, Nullable, Prize } from '@/types'
import {
  PrizeCategories,
  type TIconValue,
  type TPrizeCategoryValue,
  Themes,
} from '@/utils/helper-objects'
import { groupBy } from '@/utils/utils'
import { type SiteSlug, ThemeType, getTheme } from '@goodlifeplus/themes'
import { DateTime, Interval } from 'luxon'
import { defineAsyncComponent, defineComponent, toValue } from 'vue'
import type { RouteRecordRaw } from 'vue-router'

import SidebarBalanceCard from '../cards/SidebarBalanceCard.vue'
import { Social } from './AppSocialIcons.vue'

type NavLink = {
  route: {
    name: string
    hash?: string
    params?: Record<any, any>
    query?: Record<any, any>
  }
  category: NavLinkCategory
  icon: TIconValue
  text: string
  order: number
}

/**
 * Displays a navigation sidebar with menu links.
 *
 * @see https://www.makethingsaccessible.com/guides/site-navigation-is-not-an-aria-menu/
 */
export default defineComponent({
  components: {
    DailyGameCard,
    DepositWelcomeModal,
    SidebarLink,
    SidebarBalanceCard,
    WelcomeBonusBanner,
    AppTrustpilot: defineAsyncComponent(
      () => import('@/components/common/AppTrustpilot.vue')
    ),
  },
  props: {
    open: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['closeMenu'],
  setup() {
    const { loadPromotions, showWelcomeBanner } = usePromotions()
    const instantWinsStore = useInstantWinsStore()

    return {
      Themes,
      Social,
      routeMeta: useRouteMeta(),
      instantWinsStore,
      loadPromotions,
      showWelcomeBanner,
    }
  },
  computed: {
    gamesEnabled(): boolean {
      return SettingsService.isSectionEnabled('instant-wins')
    },
    dailyGameEnabled(): boolean {
      return SettingsService.isSectionEnabled('daily-game')
    },
    navRoutes(): Record<string, Array<NavLink>> {
      const sidebarRoutes = this.routeMeta
        .flatRoutes((route: RouteRecordRaw) =>
          toValue(route.meta?.nav?.sidebar?.display ?? false)
        )
        // Only show members zone routes to logged in users
        .filter((route: RouteRecordRaw) =>
          this.$userStore.isLoggedOut
            ? route.meta?.nav?.sidebar?.category &&
              toValue(route.meta.nav.sidebar.category) !== 'members-zone'
            : true
        )
        .map(
          (route: RouteRecordRaw): NavLink => ({
            route: {
              name: route.name as string,
            },
            category: toValue(route.meta?.nav?.sidebar?.category ?? ''),
            text: toValue(route.meta?.nav?.sidebar?.text) as string,
            icon: toValue(route.meta?.nav?.sidebar?.icon) as TIconValue,
            order: toValue(route.meta?.nav?.sidebar?.order ?? -1),
          })
        )
        .concat([
          {
            route: {
              name: 'prizes',
              query: {
                tab: PrizeCategories.Featured,
                switch: 'featured-prizes',
              },
            },
            category: 'default' as NavLinkCategory,
            text: this.$t('section.featured_prizes.heading'),
            icon:
              getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
              ThemeType.Subscription
                ? this.Icons.Present
                : this.$t('section.featured_prizes.heading_icon'),
            order: 6,
          },
          ...(this.gamesEnabled &&
          this.hasInstantGamesType(PrizeCategories.MysteryBoxes)
            ? [
                {
                  route: {
                    name: 'prizes',
                    query: { tab: 'mystery-boxes', switch: 'instant-wins' },
                  },
                  category: 'default' as NavLinkCategory,
                  text: 'Mystery Prize Boxes',
                  icon: this.Icons.Present,
                  order: 2,
                } as const,
              ]
            : []),
          ...(this.gamesEnabled &&
          this.hasInstantGamesType(PrizeCategories.InstantWins)
            ? [
                {
                  route: {
                    name: 'prizes',
                    query: { tab: 'instant-prizes', switch: 'instant-wins' },
                  },
                  category: 'default' as NavLinkCategory,
                  text: 'Instant Wins',
                  icon: this.Icons.Lightening,
                  order: 4,
                } as const,
              ]
            : []),
          ...(this.gamesEnabled &&
          this.hasInstantGamesType(PrizeCategories.InstantSpins)
            ? [
                {
                  route: {
                    name: 'prizes',
                    query: { tab: 'instant-spins', switch: 'instant-wins' },
                  },
                  category: 'default' as NavLinkCategory,
                  text: 'Prize Spins',
                  icon: this.Icons.SlotMachine,
                  order: 3,
                } as const,
              ]
            : []),
          ...(this.gamesEnabled &&
          this.hasInstantGamesType(PrizeCategories.InstantPacks)
            ? [
                {
                  route: {
                    name: 'prizes',
                    query: { tab: 'instant-packs', switch: 'instant-wins' },
                  },
                  category: 'default' as NavLinkCategory,
                  text: 'Instant Packs',
                  icon: this.Icons.CardPack,
                  order: 1,
                } as const,
              ]
            : []),
          {
            route: {
              name: 'results-and-winners',
            },
            category: 'default' as NavLinkCategory,
            text:
              getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
              ThemeType.Subscription
                ? 'Hall of Fame'
                : 'Results + Winners',
            icon: this.Icons.Trophy,
            order: 7,
          },
        ])

      const groupedSidebarRoutes = groupBy((r) => r.category, sidebarRoutes)

      for (const [group, sidebarRoutes] of Object.entries(
        groupedSidebarRoutes
      )) {
        groupedSidebarRoutes[group] = sidebarRoutes.sort(
          (a: NavLink, b: NavLink) => a.order - b.order
        )
      }

      return groupedSidebarRoutes
    },
  },
  watch: {
    '$userStore.isLoggedIn': {
      async handler(isLoggedIn: boolean) {
        if (isLoggedIn) {
          this.$emit('closeMenu')
        }
      },
    },
  },
  async mounted() {
    void this.loadPromotions()

    if (
      !this.instantWinsStore.hasLoadedAll &&
      this.instantWinsStore.instantWins.size === 0
    ) {
      void this.instantWinsStore.fetchInstantWins()
    }
  },
  methods: {
    hasInstantGamesType(type: TPrizeCategoryValue): boolean {
      const instantWinDrawsForNav =
        this.instantWinsStore.futureInstantWinDraws.filter(
          (draw) => draw.is_visible ?? true
        )

      return instantWinDrawsForNav.some((draw: InstantWinDraw) =>
        draw.main_prize.categories?.includes(type)
      )
    },
    prizeRoute(prize: Prize) {
      if (prize.campaign_code) {
        return {
          name: 'campaign',
          params: { campaignCode: prize.campaign_code },
        }
      }

      if (
        Interval.fromDateTimes(
          prize.close_at,
          DateTime.now().endOf('day')
        ).contains(DateTime.now())
      ) {
        return { name: 'live-draw', params: { prizeCode: prize.code } }
      }

      return {
        name: 'prize',
        params: { prizeCode: prize.code },
      }
    },
  },
})
</script>

<template>
  <nav class="flex flex-col gap-2 bg-pure-white p-3">
    <template v-if="open">
      <DailyGameCard
        v-if="dailyGameEnabled"
        :progress="$userStore.dailyGameProgress"
      />

      <SidebarBalanceCard
        v-if="$userStore.isLoggedIn"
        :user="$userStore.user!"
      />

      <WelcomeBonusBanner
        v-if="showWelcomeBanner"
        :show-countdown="false"
        :style="{
          '--mask-squiggle-url': `url('/assets/images/masks/welcome-squiggle-sm.svg')`,
        }"
      >
        <template #deposit-button>
          <DepositWelcomeModal :welcome-only="true">
            <template #button="{ openModal, hasAnyDepositOptions }">
              <AppButton
                class="button-thin !max-w-full button-primary !py-2 text-sm leading-4"
                title="Claim Bonus"
                :disabled="!hasAnyDepositOptions"
                @click="openModal()"
              >
                Claim Bonus
              </AppButton>
            </template>
          </DepositWelcomeModal>
        </template>
      </WelcomeBonusBanner>
    </template>

    <template v-for="(routeGroup, category) in navRoutes" :key="category">
      <ol role="list" class="flex flex-col">
        <li v-for="(routeDetails, key) in routeGroup" :key>
          <SidebarLink
            v-tooltip="{
              content: routeDetails.text,
              placement: 'right',
              popperClass: open
                ? 'hidden sidebar-link-tooltip'
                : 'block sidebar-link-tooltip',
            }"
            :route="routeDetails.route"
            :open
            :class="`route-order-${routeDetails.order}`"
            @close-menu="$emit('closeMenu')"
          >
            <template #icon>
              <AppIcon v-if="routeDetails.icon" :icon="routeDetails.icon" />
            </template>

            <template #content>
              {{ routeDetails.text }}
            </template>
          </SidebarLink>
        </li>
      </ol>
    </template>

    <template v-if="open">
      <hr />

      <AppSocialIcons
        v-if="open"
        :socials="[Social.Facebook, Social.X, Social.Tiktok, Social.Instagram]"
        :use-color-icons="true"
        size="lg"
        class="px-3"
      >
        <template #header>
          <p class="text-left font-bold">Find Us On</p>
        </template>
      </AppSocialIcons>

      <SectionGuard section="trustpilot">
        <hr class="my-1" />

        <a
          href="https://uk.trustpilot.com/review/goodlifeplus.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          class="bg-black rounded-lg p-6 text-pure-white mask-squiggle flex-wrap"
          :style="{
            '--mask-squiggle-url': `url('/assets/images/masks/trustpilot-background-sm.svg')`,
          }"
        >
          <div class="mb-2 whitespace-nowrap text-center font-semibold">
            What our customers think
          </div>

          <AppTrustpilot />
        </a>
      </SectionGuard>

      <template v-if="$userStore.isLoggedIn">
        <hr class="my-1" />
        <AppTextButton
          :size="UI.TextButton.Size.Large"
          :variant="UI.TextButton.Variant.Tertiary"
          :route="{ name: 'logout' }"
          :label="$t('label.logout')"
        />
      </template>
    </template>
  </nav>
</template>

<style lang="scss" scoped>
nav {
  z-index: 2147483640; // Higher than drawer (2147483638) and backdrop (2147483631)
}
</style>
